import React, {useState} from 'react'
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField
} from '@mui/material'
import MUIDataGrid from 'common/AbstractDataGrid/DataGridMUI'
import format from 'date-fns/format'
import { SubscriptionDetails } from 'types/api/faber/dataUsage.type'

import dataApi from "../../../../api/faber/DataApi";

import {
    CellText,
    fitTableHeight,
    listGridProps,
    MutedText,
    RowActionsMenu,
    StatusPill
} from './listUi'

const formatDate = (value?: string | number) =>
    value ? format(new Date(value), 'dd MMM yyyy, HH:mm') : '—'

function SubscriptionTable({ subscriptionData, userId, faberToken }: { subscriptionData: SubscriptionDetails[], userId: string, faberToken: string }) {
    const [openDialog, setOpenDialog] = useState(false)
    const [cancelReason, setCancelReason] = useState('')
    const [selectedSubscription, setSelectedSubscription] = useState<SubscriptionDetails | null>(null)

    const handleOpenDialog = (subscription: SubscriptionDetails) => {
        setSelectedSubscription(subscription)
        setOpenDialog(true)
    }

    const handleCloseDialog = () => {
        setCancelReason('')
        setSelectedSubscription(null)
        setOpenDialog(false)
    }

    const handleConfirmCancellation = async () => {
        if (!selectedSubscription) return

        try {
            await dataApi.cancelUserSubscription(faberToken, userId, {
                reasons: [cancelReason]
            })
            console.log('Subscription cancelled successfully')
        } catch (error) {
            console.error('Failed to cancel subscription:', error)
        } finally {
            handleCloseDialog()
        }
    }

    const subscriptionColumns = [
        {
            field: 'name',
            headerName: 'Product',
            sortable: false,
            flex: 1.2,
            minWidth: 160,
            renderCell: ({ row }) => <CellText strong>{row.name}</CellText>
        },
        {
            field: 'status',
            headerName: 'Status',
            sortable: false,
            flex: 0.7,
            minWidth: 110,
            renderCell: ({ row }) => <StatusPill status={row.status} />
        },
        {
            field: 'billingCycleAnchor',
            headerName: 'Started',
            flex: 0.9,
            minWidth: 140,
            renderCell: ({ row }) => <MutedText>{formatDate(row.billingCycleAnchor)}</MutedText>
        },
        {
            field: 'currentPeriodStart',
            headerName: 'Current Period',
            flex: 1.4,
            minWidth: 220,
            renderCell: ({ row }) => (
                <MutedText>
                    {formatDate(row.currentPeriodStart)} – {formatDate(row.currentPeriodEnd)}
                </MutedText>
            )
        },
        {
            field: 'earliestCancellationDate',
            headerName: 'Earliest Cancellation',
            flex: 0.9,
            minWidth: 150,
            renderCell: ({ row }) => <MutedText>{formatDate(row.earliestCancellationDate)}</MutedText>
        },
        {
            field: 'actions',
            headerName: '',
            sortable: false,
            flex: 0,
            width: 56,
            align: 'right' as const,
            renderCell: (params) => (
                <RowActionsMenu
                    actions={[
                        {
                            label: 'Cancel subscription',
                            danger: true,
                            onClick: () => handleOpenDialog(params.row)
                        }
                    ]}
                />
            )
        }
    ]

    const rows = Array.isArray(subscriptionData) ? subscriptionData : []

    return (
        <>
            <MUIDataGrid
                {...listGridProps}
                columns={subscriptionColumns}
                rows={rows}
                getRowId={(row) => row.name + String(row.currentPeriodStart)}
                hideFooter
                tableHeight={fitTableHeight(rows.length)}
            />

            <Dialog open={openDialog} onClose={handleCloseDialog}>
                <DialogTitle>Cancel Subscription</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Reason for cancellation"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} color="primary">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmCancellation}
                        color="error"
                        variant="contained"
                        disabled={!cancelReason.trim()} // Disable if input is empty or whitespace
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default SubscriptionTable
